import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { EnvelopeSimple, PaperPlaneTilt, CircleNotch } from '@phosphor-icons/react';

const API = '/api';

export default function EmailConfigPanel() {
  const [config, setConfig] = useState({
    provider: 'smtp', smtp_host: '', smtp_port: 587,
    smtp_user: '', smtp_pass: '', smtp_from: '', smtp_use_tls: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [configured, setConfigured] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/email-config`, { withCredentials: true });
        setConfig({
          provider: data.provider || 'smtp',
          smtp_host: data.smtp_host || '',
          smtp_port: data.smtp_port || 587,
          smtp_user: data.smtp_user || '',
          smtp_pass: '',
          smtp_from: data.smtp_from || '',
          smtp_use_tls: data.smtp_use_tls !== false,
        });
        setConfigured(data.configured || false);
        setPasswordSaved(!!data.smtp_pass_set);
      } catch (e) {
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/email-config`, config, { withCredentials: true });
      setConfigured(data.configured);
      alert(data.message);
    } catch (e) {
      alert('Error saving: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) return alert('Please enter a recipient email');
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/email-config/test`, { recipient: testEmail }, { withCredentials: true });
      setTestResult({ success: true, message: data.message });
    } catch (e) {
      setTestResult({ success: false, message: e.response?.data?.detail || e.message });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="bg-white rounded-xl p-8 border border-slate-200 text-center text-slate-500">Loading email configuration...</div>;

  return (
    <div className="space-y-6" data-testid="email-config-panel">
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
            <EnvelopeSimple size={24} className="text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>SMTP Email Configuration</h3>
            <p className="text-sm text-slate-500 mt-1">
              Configure your SMTP settings for sending emails (password resets, notifications, correspondence).
              Works with Brevo, Gmail, Office 365, Amazon SES, or any SMTP provider.
            </p>
            <div className="mt-2">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${configured ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${configured ? 'bg-green-500' : 'bg-amber-500'}`} />
                {configured ? 'Configured' : 'Not Configured'}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">SMTP Host *</Label>
              <Input value={config.smtp_host} onChange={(e) => setConfig({...config, smtp_host: e.target.value})} placeholder="e.g., smtp-relay.brevo.com" className="rounded-lg" data-testid="smtp-host" />
            </div>
            <div>
              <Label className="text-xs">SMTP Port *</Label>
              <Input type="number" value={config.smtp_port} onChange={(e) => setConfig({...config, smtp_port: parseInt(e.target.value) || 587})} className="rounded-lg" data-testid="smtp-port" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">SMTP Username *</Label>
              <Input value={config.smtp_user} onChange={(e) => setConfig({...config, smtp_user: e.target.value})} placeholder="Your SMTP username or email" className="rounded-lg" data-testid="smtp-user" />
            </div>
            <div>
              <Label className="text-xs">SMTP Password *</Label>
              <Input type="password" value={config.smtp_pass} onChange={(e) => setConfig({...config, smtp_pass: e.target.value})} placeholder={passwordSaved ? "Saved - leave blank to keep" : "Your SMTP password or API key"} className="rounded-lg" data-testid="smtp-pass" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">From Email Address *</Label>
              <Input value={config.smtp_from} onChange={(e) => setConfig({...config, smtp_from: e.target.value})} placeholder="noreply@yourdomain.com" className="rounded-lg" data-testid="smtp-from" />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={config.smtp_use_tls} onChange={(e) => setConfig({...config, smtp_use_tls: e.target.checked})} className="rounded border-slate-300" data-testid="smtp-tls" />
                <span className="text-sm text-slate-700">Use TLS/STARTTLS</span>
              </label>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg" data-testid="email-save-btn">
              {saving ? <><CircleNotch size={16} className="mr-2 animate-spin" /> Saving...</> : 'Save Configuration'}
            </Button>
          </div>

          {/* Provider Quick Setup */}
          <div className="border-t border-slate-200 pt-4 mt-4">
            <p className="text-xs text-slate-500 mb-2 uppercase font-semibold">Quick Setup Presets</p>
            <div className="flex flex-wrap gap-2">
              {[
                { name: 'Brevo (Sendinblue)', host: 'smtp-relay.brevo.com', port: 587 },
                { name: 'Gmail', host: 'smtp.gmail.com', port: 587 },
                { name: 'Office 365', host: 'smtp.office365.com', port: 587 },
                { name: 'Amazon SES', host: 'email-smtp.us-east-1.amazonaws.com', port: 587 },
                { name: 'Mailgun', host: 'smtp.mailgun.org', port: 587 },
              ].map(preset => (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() => setConfig({...config, smtp_host: preset.host, smtp_port: preset.port})}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700 transition-all"
                  data-testid={`preset-${preset.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Test Email */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
            <PaperPlaneTilt size={24} className="text-emerald-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Test Email</h3>
            <p className="text-sm text-slate-500 mt-1">Send a test email to verify your configuration is working correctly.</p>
            <div className="flex gap-3 mt-4 max-w-md">
              <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@example.com" type="email" className="rounded-lg" data-testid="test-email-input" />
              <Button onClick={handleTest} disabled={testing || !configured} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shrink-0" data-testid="test-email-btn">
                {testing ? <><CircleNotch size={16} className="mr-2 animate-spin" /> Sending...</> : <><PaperPlaneTilt size={16} className="mr-2" /> Send Test</>}
              </Button>
            </div>
            {testResult && (
              <div className={`mt-3 p-3 rounded-lg text-sm ${testResult.success ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`} data-testid="test-result">
                {testResult.message}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
